import * as React from "react"
import { Routes, Route, Navigate, Link, useParams, useNavigate } from "react-router-dom"
import AdsComponent from './AdsComponent';
import { FaMoon, FaSun, FaSync } from "react-icons/fa"
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

// --- Types ---

export interface RateData {
  name: string;
  key: string;
  buy: number | null;
  sell: number | null;
  avg: number | null;
  buyFixed: string;
  sellFixed: string;
  avgFixed: string;
  change: string;
  direction: 'up' | 'down' | 'neutral';
  source: string;
  lastUpdated: string;
}

export interface DashboardData {
  rates: Record<string, RateData>;
  fetchedAt: string;
  providers: string[];
}

interface HistorySnapshot {
  timestamp: string;
  rates: Record<string, { buy: number | null; sell: number | null }>;
}

interface HistoryData {
  snapshots: HistorySnapshot[];
}

interface BluelyticsEntry {
  date: string;
  source: string;
  value_sell: number;
  value_buy: number;
}

interface AwesomeApiHistEntry {
  bid: string;
  ask: string;
  timestamp: string;
  create_date?: string;
}

interface NewsArticle {
  title: string;
  slug: string;
  summary: string;
  body: string;
  category: string;
  date: string;
  country: string;
}

interface NewsPayload {
  articles: NewsArticle[];
  generatedAt: string;
  country: string;
}

const getTodayUTC = (): string => new Date().toISOString().slice(0, 10);
const getYesterdayUTC = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const CATEGORY_COLORS: Record<string, string> = {
  'Economía': '#3fb950',
  'Economia': '#3fb950',
  'Mercados': '#58a6ff',
  'Política Monetaria': '#d2a8ff',
  'Comercio Exterior': '#f0883e',
  'Finanças': '#3fb950',
  'Finanzas': '#f85149',
};

// Maps country currency to AwesomeAPI pair for historical data
const AWESOME_API_PAIRS: Record<string, string> = {
  br: 'USD-BRL',
  mx: 'USD-MXN',
  cl: 'USD-CLP',
  co: 'USD-COP',
  uy: 'USD-UYU',
  bo: 'USD-BOB',
  // VE uses VEF which is stale; skip
};

type Theme = 'dark' | 'light';
type ChartPeriod = '1D' | '5D' | '1M' | '3M' | '6M';

// --- Country Config ---

interface CountryConfig {
  code: string;
  name: string;
  flag: string;
  currency: string;
  currencySymbol: string;
  locale: string;
  defaultRate: string;
  subtitle: string;
}

const COUNTRY_LIST: CountryConfig[] = [
  { code: 'ar', name: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}', currency: 'ARS', currencySymbol: '$', locale: 'es-AR', defaultRate: 'blue', subtitle: 'Cotizaciones del d\u00f3lar en Argentina' },
  { code: 'br', name: 'Brasil', flag: '\u{1F1E7}\u{1F1F7}', currency: 'BRL', currencySymbol: 'R$', locale: 'pt-BR', defaultRate: 'comercial', subtitle: 'Cota\u00e7\u00f5es do d\u00f3lar no Brasil' },
  { code: 'mx', name: 'M\u00e9xico', flag: '\u{1F1F2}\u{1F1FD}', currency: 'MXN', currencySymbol: '$', locale: 'es-MX', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en M\u00e9xico' },
  { code: 'cl', name: 'Chile', flag: '\u{1F1E8}\u{1F1F1}', currency: 'CLP', currencySymbol: '$', locale: 'es-CL', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en Chile' },
  { code: 'co', name: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}', currency: 'COP', currencySymbol: '$', locale: 'es-CO', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en Colombia' },
  { code: 've', name: 'Venezuela', flag: '\u{1F1FB}\u{1F1EA}', currency: 'VES', currencySymbol: 'Bs', locale: 'es-VE', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en Venezuela' },
  { code: 'uy', name: 'Uruguay', flag: '\u{1F1FA}\u{1F1FE}', currency: 'UYU', currencySymbol: '$U', locale: 'es-UY', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en Uruguay' },
  { code: 'bo', name: 'Bolivia', flag: '\u{1F1E7}\u{1F1F4}', currency: 'BOB', currencySymbol: 'Bs', locale: 'es-BO', defaultRate: 'oficial', subtitle: 'Cotizaciones del d\u00f3lar en Bolivia' },
];

const VALID_CODES = new Set(COUNTRY_LIST.map(c => c.code));

function getCountryConfig(code: string): CountryConfig | undefined {
  return COUNTRY_LIST.find(c => c.code === code);
}

// --- Theme ---

const getStoredTheme = (): Theme => {
  try {
    const stored = localStorage.getItem('dblue-theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  return 'dark';
};

const themes = {
  dark: {
    bg: '#0d1117',
    cardBg: '#161b22',
    cardBorder: '#21262d',
    headerBg: '#0d1117',
    headerBorder: '#21262d',
    text: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#484f58',
    green: '#3fb950',
    red: '#f85149',
    chartLine: '#3fb950',
    chartGrid: '#21262d',
    chartTooltipBg: '#1c2128',
    selectedRowBg: '#1c2128',
    tableBorder: '#21262d',
    buttonBg: '#21262d',
    buttonBorder: '#363b42',
    activePeriodBg: '#30363d',
    flagActiveBg: '#30363d',
  },
  light: {
    bg: '#f6f8fa',
    cardBg: '#ffffff',
    cardBorder: '#d1d9e0',
    headerBg: '#ffffff',
    headerBorder: '#d1d9e0',
    text: '#1f2328',
    textSecondary: '#636c76',
    textMuted: '#8b949e',
    green: '#1a7f37',
    red: '#cf222e',
    chartLine: '#1a7f37',
    chartGrid: '#d1d9e0',
    chartTooltipBg: '#ffffff',
    selectedRowBg: '#f0f3f6',
    tableBorder: '#d1d9e0',
    buttonBg: '#f6f8fa',
    buttonBorder: '#d1d9e0',
    activePeriodBg: '#e2e5e9',
    flagActiveBg: '#e2e5e9',
  },
};

type ThemeColors = typeof themes.dark;

// --- Helpers ---

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif";

const fmtMoney = (value: string | number | null, locale: string = 'es-AR'): string => {
  if (value === null || value === '-') return '\u2014';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '\u2014';
  return num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatTime = (iso: string, locale: string = 'es-AR') => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(locale, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const getCutoff = (period: ChartPeriod): number => {
  const now = Date.now();
  const DAY = 86400000;
  switch (period) {
    case '1D': return now - DAY;
    case '5D': return now - 5 * DAY;
    case '1M': return now - 30 * DAY;
    case '3M': return now - 90 * DAY;
    case '6M': return now - 180 * DAY;
  }
};

const formatChartDate = (ts: number, period: ChartPeriod, locale: string): string => {
  const d = new Date(ts);
  if (period === '1D' || period === '5D') {
    return d.toLocaleString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString(locale, { day: 'numeric', month: 'short' });
};

// --- Components ---

const Header = ({ theme, toggleTheme, onReload, activeCountry, t }: {
  theme: Theme; toggleTheme: () => void; onReload: () => void; activeCountry: string; t: ThemeColors;
}) => {
  const config = getCountryConfig(activeCountry);

  return (
    <header style={{
      borderBottom: `1px solid ${t.headerBorder}`,
      background: t.headerBg,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      <div style={{
        maxWidth: 1120, margin: '0 auto', padding: '10px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 12 }} aria-label="Navegaci\u00f3n principal">
          <Link to="/ar" style={{ textDecoration: 'none' }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: t.text, letterSpacing: '-0.02em', margin: 0 }}>
              Dólar Blue Hoy
            </h1>
          </Link>

          {/* Country Flags */}
          <div style={{ display: 'flex', gap: 2, alignItems: 'center', marginLeft: 8 }}>
            {COUNTRY_LIST.map(c => (
              <Link
                key={c.code}
                to={`/${c.code}`}
                aria-label={c.name}
                title={c.name}
                style={{
                  textDecoration: 'none',
                  fontSize: 20,
                  padding: '4px 6px',
                  borderRadius: 4,
                  background: c.code === activeCountry ? t.flagActiveBg : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {c.flag}
              </Link>
            ))}
          </div>
        </nav>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={toggleTheme} aria-label="Cambiar tema claro/oscuro" style={{
            background: t.buttonBg, border: `1px solid ${t.buttonBorder}`, borderRadius: 6,
            padding: '6px 10px', cursor: 'pointer', color: t.textSecondary, fontSize: 14,
            display: 'flex', alignItems: 'center',
          }}>
            {theme === 'dark' ? <FaSun /> : <FaMoon />}
          </button>
          <button onClick={onReload} aria-label="Recargar cotizaciones" style={{
            background: t.buttonBg, border: `1px solid ${t.buttonBorder}`, borderRadius: 6,
            padding: '6px 12px', cursor: 'pointer', color: t.textSecondary, fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT,
          }}>
            <FaSync style={{ fontSize: 10 }} /> Recargar
          </button>
        </div>
      </div>
      {config && (
        <div style={{
          maxWidth: 1120, margin: '0 auto', padding: '0 24px 8px',
          fontSize: 11, color: t.textMuted,
        }}>
          {config.subtitle}
        </div>
      )}
    </header>
  );
};

export const HeroSection = ({ rate, currencySymbol, locale, t }: {
  rate: RateData; currencySymbol: string; locale: string; t: ThemeColors;
}) => {
  const spread = (rate.buy !== null && rate.sell !== null)
    ? rate.sell - rate.buy
    : null;
  const changeColor = rate.direction === 'up' ? t.green : rate.direction === 'down' ? t.red : t.textMuted;

  return (
    <section style={{ flex: 1, minWidth: 300 }} aria-label={`Cotizaci\u00f3n ${rate.name}`}>
      <h2 style={{ fontSize: 14, color: t.textSecondary, marginBottom: 8, fontWeight: 500, margin: '0 0 8px 0' }}>
        {rate.name}
      </h2>

      <div>
        {rate.buy !== null && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 36, fontWeight: 700, color: t.text, fontVariantNumeric: 'tabular-nums' }}>
              {currencySymbol}{fmtMoney(rate.buy, locale)}
            </span>
            <span style={{ fontSize: 11, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Compra
            </span>
            {rate.change && (
              <span style={{ fontSize: 14, fontWeight: 600, color: changeColor }}>
                {rate.direction === 'up' ? '\u25B2' : rate.direction === 'down' ? '\u25BC' : ''} {rate.change}
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 36, fontWeight: 700, color: t.text, fontVariantNumeric: 'tabular-nums' }}>
            {currencySymbol}{fmtMoney(rate.sell, locale)}
          </span>
          <span style={{ fontSize: 11, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Venta
          </span>
          {rate.buy === null && rate.change && (
            <span style={{ fontSize: 14, fontWeight: 600, color: changeColor }}>
              {rate.direction === 'up' ? '\u25B2' : rate.direction === 'down' ? '\u25BC' : ''} {rate.change}
            </span>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: t.textSecondary }}>
        <span>
          Spread: <strong style={{ color: t.text }}>{spread !== null ? `${currencySymbol}${fmtMoney(spread, locale)}` : '\u2014'}</strong>
        </span>
      </div>
    </section>
  );
};

const CustomTooltip = ({ active, payload, t, currencySymbol, locale }: any) => {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  return (
    <div style={{
      background: t.chartTooltipBg, border: `1px solid ${t.cardBorder}`,
      borderRadius: 6, padding: '8px 12px', fontSize: 12, color: t.text,
    }}>
      <div style={{ color: t.textMuted, marginBottom: 4 }}>
        {new Date(point.payload.timestamp).toLocaleString(locale)}
      </div>
      <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {currencySymbol}{fmtMoney(point.value, locale)}
      </div>
    </div>
  );
};

export const ChartSection = ({ chartData, period, setPeriod, rateName, locale, currencySymbol, t }: {
  chartData: { timestamp: number; sell: number }[];
  period: ChartPeriod;
  setPeriod: (p: ChartPeriod) => void;
  rateName: string;
  locale: string;
  currencySymbol: string;
  t: ThemeColors;
}) => {
  const periods: ChartPeriod[] = ['1D', '5D', '1M', '3M', '6M'];

  return (
    <section aria-label={`Gr\u00e1fico hist\u00f3rico de ${rateName}`} style={{
      flex: 1, minWidth: 300, background: t.cardBg,
      border: `1px solid ${t.cardBorder}`, borderRadius: 8, padding: 16,
    }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {periods.map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{
            background: p === period ? t.activePeriodBg : 'transparent',
            border: 'none', borderRadius: 4, padding: '4px 10px',
            color: p === period ? t.text : t.textMuted,
            fontSize: 12, fontWeight: p === period ? 600 : 400,
            cursor: 'pointer', fontFamily: FONT,
          }}>
            {p}
          </button>
        ))}
      </div>

      {chartData.length > 1 ? (() => {
        const sells = chartData.map(d => d.sell);
        const min = Math.min(...sells);
        const max = Math.max(...sells);
        const range = max - min || max * 0.05;
        const padding = range * 0.15;
        const yMin = Math.max(0, min - padding);
        const yMax = max + padding;
        return (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData}>
            <XAxis
              dataKey="timestamp"
              tickFormatter={(ts) => formatChartDate(ts, period, locale)}
              stroke={t.textMuted}
              tick={{ fontSize: 10, fill: t.textMuted }}
              axisLine={{ stroke: t.chartGrid }}
              tickLine={false}
              minTickGap={40}
            />
            <YAxis
              domain={[yMin, yMax]}
              stroke={t.textMuted}
              tick={{ fontSize: 10, fill: t.textMuted }}
              axisLine={false}
              tickLine={false}
              width={70}
              tickFormatter={(v) => `${currencySymbol}${fmtMoney(v, locale)}`}
            />
            <Tooltip content={<CustomTooltip t={t} currencySymbol={currencySymbol} locale={locale} />} />
            <Line
              type="monotone"
              dataKey="sell"
              stroke={t.chartLine}
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
        );
      })() : (
        <div style={{
          height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: t.textMuted, fontSize: 13,
        }}>
          Datos históricos no disponibles para {rateName}
        </div>
      )}
    </section>
  );
};

export const RateTable = ({ rates, selectedKey, onSelect, currencySymbol, locale, t }: {
  rates: [string, RateData][];
  selectedKey: string;
  onSelect: (key: string) => void;
  currencySymbol: string;
  locale: string;
  t: ThemeColors;
}) => {
  const cellStyle: React.CSSProperties = {
    padding: '12px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 14,
  };
  const headerStyle: React.CSSProperties = {
    ...cellStyle, fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: t.textMuted, borderBottom: `1px solid ${t.tableBorder}`,
  };

  return (
    <section aria-label="Tabla de cotizaciones" style={{
      background: t.cardBg, border: `1px solid ${t.cardBorder}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }} role="table">
        <thead>
          <tr>
            <th style={{ ...headerStyle, textAlign: 'left' }}>Nombre</th>
            <th style={headerStyle}>Cambio</th>
            <th style={headerStyle}>Compra</th>
            <th style={headerStyle}>Venta</th>
            <th style={headerStyle}>Promedio</th>
            <th style={headerStyle}>Spread</th>
          </tr>
        </thead>
        <tbody>
          {rates.map(([key, rate]) => {
            const isSelected = key === selectedKey;
            const changeColor = rate.direction === 'up' ? t.green : rate.direction === 'down' ? t.red : t.textMuted;
            const spread = (rate.buy !== null && rate.sell !== null)
              ? rate.sell - rate.buy
              : null;

            return (
              <tr
                key={key}
                onClick={() => onSelect(key)}
                role="button"
                aria-label={`Ver cotizaci\u00f3n ${rate.name}`}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(key); }}
                style={{
                  cursor: 'pointer',
                  background: isSelected ? t.selectedRowBg : 'transparent',
                  borderBottom: `1px solid ${t.tableBorder}`,
                }}
              >
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500, color: t.text }}>
                  {rate.name}
                </td>
                <td style={{ ...cellStyle, color: changeColor, fontWeight: 500, fontSize: 13 }}>
                  {rate.change ? (
                    <>{rate.direction === 'up' ? '\u25B2' : rate.direction === 'down' ? '\u25BC' : ''} {rate.change}</>
                  ) : '\u2014'}
                </td>
                <td style={{ ...cellStyle, color: t.text }}>
                  {rate.buy !== null ? `${currencySymbol}${fmtMoney(rate.buy, locale)}` : '\u2014'}
                </td>
                <td style={{ ...cellStyle, color: t.text, fontWeight: 600 }}>
                  {currencySymbol}{fmtMoney(rate.sell, locale)}
                </td>
                <td style={{ ...cellStyle, color: t.textSecondary }}>
                  {currencySymbol}{fmtMoney(rate.avg, locale)}
                </td>
                <td style={{ ...cellStyle, color: spread !== null ? t.textSecondary : t.textMuted, fontSize: 13 }}>
                  {spread !== null ? `${currencySymbol}${fmtMoney(spread, locale)}` : '\u2014'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

// --- News Components ---

const NewsCard = ({ article, countryCode, t }: {
  article: NewsArticle; countryCode: string; t: ThemeColors;
}) => {
  const catColor = CATEGORY_COLORS[article.category] || t.textMuted;
  return (
    <Link
      to={`/${countryCode}/${article.date}/${article.slug}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <article style={{
        background: t.cardBg, border: `1px solid ${t.cardBorder}`,
        borderRadius: 8, overflow: 'hidden', height: '100%',
      }}>
        <div style={{ height: 6, background: catColor }} />
        <div style={{ padding: '16px 16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
              color: catColor, background: `${catColor}18`, padding: '2px 8px', borderRadius: 3,
            }}>
              {article.category}
            </span>
            <span style={{ fontSize: 11, color: t.textMuted }}>{article.date}</span>
          </div>
          <h3 style={{
            fontSize: 14, fontWeight: 600, color: t.text, margin: '0 0 8px',
            lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {article.title}
          </h3>
          <p style={{
            fontSize: 12, color: t.textSecondary, margin: 0, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {article.summary}
          </p>
        </div>
      </article>
    </Link>
  );
};

const NewsSection = ({ countryCode, t }: { countryCode: string; t: ThemeColors }) => {
  const [news, setNews] = React.useState<NewsArticle[]>([]);

  React.useEffect(() => {
    const today = getTodayUTC();
    const yesterday = getYesterdayUTC();

    fetch(`/${countryCode}/${today}/news.json`)
      .then(r => r.ok ? r.json() : Promise.reject('not found'))
      .then((payload: NewsPayload) => setNews(payload.articles || []))
      .catch(() => {
        // Fall back to yesterday
        fetch(`/${countryCode}/${yesterday}/news.json`)
          .then(r => r.ok ? r.json() : null)
          .then((payload: NewsPayload | null) => {
            if (payload?.articles) setNews(payload.articles);
          })
          .catch(() => {}); // silently fail — no news yet
      });
  }, [countryCode]);

  if (news.length === 0) return null;

  return (
    <section aria-label="Noticias relevantes" style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: t.text, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        Noticias Relevantes
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: 16,
      }}>
        {news.map((article) => (
          <NewsCard key={article.slug} article={article} countryCode={countryCode} t={t} />
        ))}
      </div>
    </section>
  );
};

// --- FAQ Section (Argentina only) ---

const FAQ_ITEMS = [
  {
    q: '¿Qué es el dólar blue?',
    a: 'El dólar blue es el tipo de cambio del dólar estadounidense en el mercado paralelo o informal de Argentina. Opera fuera del sistema bancario oficial y su precio se determina por la oferta y demanda en cuevas y casas de cambio no reguladas. Históricamente surge como respuesta a las restricciones cambiarias (cepo) que limitan la compra de dólares oficiales.',
  },
  {
    q: '¿Cuál es la diferencia entre el dólar oficial y el dólar blue?',
    a: 'El dólar oficial es el tipo de cambio regulado por el Banco Central de la República Argentina (BCRA) y se utiliza en operaciones bancarias formales. El dólar blue cotiza en el mercado informal sin regulación estatal. La diferencia entre ambos se conoce como "brecha cambiaria" y puede variar significativamente según el contexto económico.',
  },
  {
    q: '¿Qué es el dólar MEP?',
    a: 'El dólar MEP (Mercado Electrónico de Pagos), también llamado dólar bolsa, es un tipo de cambio que se obtiene comprando bonos en pesos en la bolsa argentina y vendiéndolos en dólares. Es una operación legal que permite acceder a dólares sin las restricciones del mercado oficial, aunque con un precio superior al oficial.',
  },
  {
    q: '¿Qué es el dólar CCL?',
    a: 'El dólar Contado con Liquidación (CCL) es similar al MEP, pero la liquidación se realiza en una cuenta en el exterior. Se compran activos en pesos en Argentina y se venden en dólares en mercados internacionales. Es utilizado principalmente por empresas e inversores que necesitan transferir divisas fuera del país de forma legal.',
  },
  {
    q: '¿Cada cuánto se actualizan las cotizaciones?',
    a: 'Las cotizaciones en Dólar Blue Hoy se actualizan automáticamente cada 2 minutos durante los días hábiles (lunes a viernes). Los datos provienen de múltiples fuentes financieras confiables que son consultadas en tiempo real para ofrecer la información más precisa disponible.',
  },
];

const FaqSection = ({ t }: { t: ThemeColors }) => (
  <section aria-label="Preguntas frecuentes" style={{ marginTop: 32 }}>
    <h2 style={{ fontSize: 16, fontWeight: 600, color: t.text, margin: '0 0 16px' }}>
      Preguntas Frecuentes
    </h2>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {FAQ_ITEMS.map((item, i) => (
        <details key={i} style={{
          background: t.cardBg, border: `1px solid ${t.cardBorder}`,
          borderRadius: 8, overflow: 'hidden',
        }}>
          <summary style={{
            padding: '14px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            color: t.text, listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {item.q}
            <span style={{ color: t.textMuted, fontSize: 12, marginLeft: 8, flexShrink: 0 }}>+</span>
          </summary>
          <div style={{
            padding: '0 16px 16px', fontSize: 13, lineHeight: 1.7, color: t.textSecondary,
          }}>
            {item.a}
          </div>
        </details>
      ))}
    </div>

    {/* FAQPage schema */}
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    })}} />
  </section>
);

// --- Article Page ---

const ArticlePage = ({ theme, toggleTheme, t }: {
  theme: Theme; toggleTheme: () => void; t: ThemeColors;
}) => {
  const { country: countryCode, date, slug } = useParams<{ country: string; date: string; slug: string }>();
  const navigate = useNavigate();
  const config = getCountryConfig(countryCode || 'ar');
  const [article, setArticle] = React.useState<NewsArticle | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!countryCode || !date || !slug) return;
    fetch(`/${countryCode}/${date}/news.json`)
      .then(r => r.ok ? r.json() : Promise.reject('not found'))
      .then((payload: NewsPayload) => {
        const found = payload.articles?.find(a => a.slug === slug);
        setArticle(found || null);
      })
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [countryCode, date, slug]);

  // SEO: update head for article
  React.useEffect(() => {
    if (!article) return;
    document.title = `${article.title} | Dólar Blue Hoy`;

    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', article.summary);
    setMeta('meta[property="og:title"]', article.title);
    setMeta('meta[property="og:description"]', article.summary);
    setMeta('meta[property="og:url"]', `https://dolar.blue/${countryCode}/${date}/${slug}`);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://dolar.blue/${countryCode}/${date}/${slug}`);

    // NewsArticle schema
    const ldScript = document.querySelector('script[type="application/ld+json"]');
    if (ldScript) {
      try {
        const schema = {
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          description: article.summary,
          datePublished: article.date,
          dateModified: article.date,
          author: { '@type': 'Organization', name: 'Dólar Blue Hoy' },
          publisher: { '@type': 'Organization', name: 'Dólar Blue Hoy', logo: 'https://dolar.blue/android-chrome-512x512.png' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://dolar.blue/${countryCode}/${date}/${slug}` },
        };
        ldScript.textContent = JSON.stringify(schema);
      } catch {}
    }
  }, [article, countryCode, date, slug]);

  const catColor = article ? (CATEGORY_COLORS[article.category] || t.textMuted) : t.textMuted;

  return (
    <div style={{ minHeight: '100vh', background: t.bg, fontFamily: FONT, color: t.text }}>
      <Header theme={theme} toggleTheme={toggleTheme} onReload={() => {}} activeCountry={countryCode || 'ar'} t={t} />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 64px' }}>
        <Link to={`/${countryCode}`} style={{
          color: t.textSecondary, textDecoration: 'none', fontSize: 13, display: 'inline-block', marginBottom: 24,
        }}>
          &larr; Volver a cotizaciones
        </Link>

        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: t.textSecondary, fontSize: 14 }}>
            Cargando artículo...
          </div>
        )}

        {!loading && !article && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ color: t.textSecondary, fontSize: 16, marginBottom: 16 }}>Artículo no encontrado</p>
            <Link to={`/${countryCode}`} style={{ color: t.green, textDecoration: 'none' }}>
              Volver al inicio
            </Link>
          </div>
        )}

        {!loading && article && (
          <article>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                color: catColor, background: `${catColor}18`, padding: '3px 10px', borderRadius: 4,
              }}>
                {article.category}
              </span>
              <span style={{ fontSize: 12, color: t.textMuted }}>{article.date}</span>
              {config && <span style={{ fontSize: 12, color: t.textMuted }}>{config.name}</span>}
            </div>

            <h1 style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.3, margin: '0 0 20px', color: t.text }}>
              {article.title}
            </h1>

            <p style={{ fontSize: 16, color: t.textSecondary, marginBottom: 24, lineHeight: 1.5, fontStyle: 'italic' }}>
              {article.summary}
            </p>

            <div style={{ fontSize: 16, lineHeight: 1.8, color: t.text }}>
              {article.body.split('\n\n').map((paragraph, i) => (
                <p key={i} style={{ marginBottom: 16 }}>{paragraph}</p>
              ))}
            </div>
          </article>
        )}

        <aside style={{ marginTop: 32 }} aria-label="Publicidad">
          <AdsComponent dataAdSlot='4458659545' />
        </aside>
      </main>
    </div>
  );
};

// --- Content Pages ---

interface ContentSection {
  heading: string;
  body: string;
}

interface ContentPageConfig {
  path: string;
  title: string;
  metaDescription: string;
  canonical: string;
  h1: string;
  intro: string;
  sections: ContentSection[];
  relatedLinks: { label: string; to: string }[];
}

const CONTENT_PAGES: ContentPageConfig[] = [
  {
    path: '/ar/que-es-el-dolar-blue',
    title: '¿Qué es el Dólar Blue? Guía Completa | Dólar Blue Hoy',
    metaDescription: 'Qué es el dólar blue en Argentina, por qué existe, cómo se compra, cuál es la diferencia con el oficial y cómo afecta la economía. Guía completa actualizada.',
    canonical: 'https://dolar.blue/ar/que-es-el-dolar-blue',
    h1: '¿Qué es el Dólar Blue?',
    intro: 'El dólar blue es el tipo de cambio del dólar estadounidense en el mercado paralelo o informal de Argentina. Su precio se determina por la oferta y demanda fuera del sistema bancario oficial, y es uno de los indicadores económicos más seguidos del país.',
    sections: [
      {
        heading: '¿Por qué existe el dólar blue?',
        body: 'El dólar blue surge como consecuencia de las restricciones cambiarias impuestas por el gobierno argentino, conocidas popularmente como "cepo cambiario". Cuando el acceso a la compra de dólares oficiales se limita o se encarece con impuestos adicionales, se genera un mercado paralelo donde la divisa se comercializa a un precio diferente del oficial.\n\nHistóricamente, Argentina ha implementado controles de cambio en múltiples oportunidades: durante los años 2011-2015 y nuevamente desde 2019. En cada período de restricciones, el mercado informal se activa como válvula de escape para quienes necesitan acceder a divisas extranjeras sin las limitaciones del mercado formal.',
      },
      {
        heading: '¿Cómo se determina el precio del dólar blue?',
        body: 'A diferencia del dólar oficial, cuyo precio es regulado por el Banco Central de la República Argentina (BCRA), el dólar blue se rige exclusivamente por la oferta y la demanda en el mercado informal.\n\nLos principales factores que influyen en su cotización son: la política monetaria del BCRA (emisión de pesos, tasa de interés), la inflación, las expectativas económicas, los flujos de capital internacional, el precio de los commodities (especialmente la soja) y las decisiones de política fiscal del gobierno.\n\nLa diferencia entre el precio del dólar oficial y el blue se conoce como "brecha cambiaria", y es un indicador clave de las tensiones económicas del país.',
      },
      {
        heading: '¿Dónde se compra y vende el dólar blue?',
        body: 'El dólar blue se comercializa en casas de cambio informales conocidas como "cuevas", ubicadas principalmente en el microcentro de Buenos Aires y otras grandes ciudades argentinas. También se negocian operaciones entre particulares conocidos como "arbolitos" en la calle Florida y alrededores.\n\nEs importante destacar que las operaciones en el mercado blue son técnicamente ilegales según la normativa cambiaria argentina, aunque su práctica está ampliamente extendida y es socialmente aceptada.',
      },
      {
        heading: 'Diferencia entre dólar blue, MEP y CCL',
        body: 'Además del blue, existen otros tipos de cambio que permiten acceder a dólares en Argentina:\n\nEl dólar MEP (Mercado Electrónico de Pagos) se obtiene comprando bonos en pesos y vendiéndolos en dólares, todo dentro del mercado bursátil argentino. Es legal y su precio suele ubicarse entre el oficial y el blue.\n\nEl dólar CCL (Contado con Liquidación) es similar al MEP, pero los dólares se liquidan en una cuenta en el exterior. Es utilizado principalmente por empresas e inversores institucionales.\n\nAmbos son mecanismos legales que funcionan como alternativas al mercado paralelo, aunque con costos operativos y tiempos de liquidación que el blue no tiene.',
      },
    ],
    relatedLinks: [
      { label: 'Ver cotización del dólar blue hoy', to: '/ar' },
      { label: 'Dólar MEP vs CCL: ¿Cuál conviene?', to: '/ar/dolar-mep-vs-ccl' },
      { label: 'Glosario de términos financieros', to: '/glosario' },
    ],
  },
  {
    path: '/ar/dolar-mep-vs-ccl',
    title: 'Dólar MEP vs CCL: Diferencias, Ventajas y Cuál Conviene | Dólar Blue Hoy',
    metaDescription: 'Comparación completa entre dólar MEP y CCL: cómo funcionan, diferencias de precio, tiempos de liquidación, ventajas y cuál conviene según tu situación.',
    canonical: 'https://dolar.blue/ar/dolar-mep-vs-ccl',
    h1: 'Dólar MEP vs CCL: ¿Cuál Conviene?',
    intro: 'El dólar MEP y el dólar CCL son dos mecanismos legales para comprar dólares en Argentina a través del mercado bursátil. Aunque comparten similitudes, tienen diferencias clave en liquidación, precio y uso que determinan cuál es más conveniente según cada situación.',
    sections: [
      {
        heading: '¿Qué es el dólar MEP?',
        body: 'El dólar MEP (Mercado Electrónico de Pagos), también llamado "dólar bolsa", es un tipo de cambio implícito que surge de comprar un bono o acción en pesos argentinos y venderlo en su versión en dólares dentro del mercado local.\n\nEl bono más utilizado para esta operación es el AL30 (Bono del Tesoro Nacional en dólares con vencimiento en 2030). El inversor compra AL30 en pesos (AL30) y lo vende en dólares (AL30D), obteniendo así dólares que se acreditan en su cuenta comitente en Argentina.\n\nLa operación tiene un "parking" regulatorio: se debe mantener el bono al menos un día hábil antes de poder venderlo en la especie en dólares.',
      },
      {
        heading: '¿Qué es el dólar CCL?',
        body: 'El dólar CCL (Contado con Liquidación) funciona de manera similar al MEP, pero con una diferencia fundamental: los dólares se liquidan en una cuenta bancaria en el exterior.\n\nEl proceso implica comprar un activo que cotiza tanto en Argentina como en mercados internacionales (como acciones de empresas argentinas que cotizan en Nueva York en forma de ADRs, o bonos soberanos). Se compra en pesos en Argentina y se vende en dólares en el exterior.\n\nEl CCL es el mecanismo preferido por empresas que necesitan girar divisas al exterior y por inversores que quieren tener sus dólares fuera del sistema financiero argentino.',
      },
      {
        heading: 'Diferencias clave entre MEP y CCL',
        body: 'Liquidación: El MEP deposita dólares en una cuenta local (Argentina). El CCL los deposita en una cuenta en el exterior.\n\nPrecio: El CCL suele cotizar levemente por encima del MEP porque incluye el "premio" de tener los dólares fuera del país.\n\nPlazo: Ambos requieren un parking de al menos 1 día hábil. El CCL puede demorar más por la liquidación internacional.\n\nAccesibilidad: El MEP es más accesible para personas físicas. El CCL es más utilizado por empresas e inversores sofisticados.\n\nRegulación: Ambos son legales, pero existen limitaciones regulatorias. Quien opera CCL no puede comprar dólares oficiales (MEP o solidario) por 90 días.',
      },
      {
        heading: '¿Cuál conviene?',
        body: 'Si necesitás dólares para ahorrar y mantenerlos en Argentina, el MEP es la opción más simple y económica. Si necesitás transferir dólares al exterior (para pagos internacionales, inversiones en el extranjero, o simplemente tener divisas fuera del país), el CCL es la alternativa adecuada.\n\nPara montos pequeños de ahorro personal, el MEP es casi siempre la mejor opción por su menor costo y simplicidad operativa. Para empresas con operaciones internacionales, el CCL es frecuentemente la única vía legal disponible.',
      },
    ],
    relatedLinks: [
      { label: 'Ver cotización MEP y CCL hoy', to: '/ar' },
      { label: '¿Qué es el dólar blue?', to: '/ar/que-es-el-dolar-blue' },
      { label: 'Glosario de términos financieros', to: '/glosario' },
    ],
  },
  {
    path: '/glosario',
    title: 'Glosario de Términos Financieros y Cambiarios | Dólar Blue Hoy',
    metaDescription: 'Glosario completo de términos financieros y cambiarios: dólar blue, MEP, CCL, brecha cambiaria, cepo, BCRA, tipo de cambio, devaluación y más.',
    canonical: 'https://dolar.blue/glosario',
    h1: 'Glosario de Términos Financieros',
    intro: 'Definiciones claras y concisas de los términos financieros y cambiarios más importantes de Latinoamérica.',
    sections: [
      { heading: 'BCRA', body: 'Banco Central de la República Argentina. Entidad encargada de la política monetaria, la regulación cambiaria y la emisión de pesos. Fija la tasa de interés de referencia y administra las reservas internacionales del país.' },
      { heading: 'Brecha cambiaria', body: 'Diferencia porcentual entre el tipo de cambio oficial y el paralelo (blue). Una brecha alta indica tensiones en el mercado cambiario y restricciones al acceso de divisas. Se calcula como: (dólar blue - dólar oficial) / dólar oficial × 100.' },
      { heading: 'Cepo cambiario', body: 'Conjunto de restricciones impuestas por el gobierno para limitar la compra de moneda extranjera. Incluye topes mensuales de compra, impuestos adicionales (impuesto PAIS, retención de ganancias) y requisitos burocráticos para acceder al mercado oficial de cambios.' },
      { heading: 'Contado con liquidación (CCL)', body: 'Tipo de cambio implícito que surge de comprar activos financieros en pesos en Argentina y venderlos en dólares en mercados internacionales. Permite transferir divisas al exterior de forma legal.' },
      { heading: 'Crawling peg', body: 'Sistema cambiario en el que el banco central permite una depreciación gradual y predecible de la moneda local frente al dólar. Argentina ha utilizado este mecanismo en varios períodos para intentar controlar la inflación.' },
      { heading: 'Devaluación', body: 'Reducción del valor de una moneda frente a otra divisa, generalmente el dólar. Puede ser gradual (crawling peg) o abrupta (salto discreto). En Argentina, las devaluaciones abruptas suelen generar aumentos de precios e incertidumbre económica.' },
      { heading: 'Dólar blue', body: 'Tipo de cambio del dólar estadounidense en el mercado paralelo o informal de Argentina. Se comercializa fuera del sistema bancario oficial y su precio se determina por oferta y demanda. Es ilegal pero ampliamente utilizado.' },
      { heading: 'Dólar MEP', body: 'Dólar Mercado Electrónico de Pagos, también llamado dólar bolsa. Se obtiene comprando bonos en pesos y vendiéndolos en dólares dentro del mercado bursátil argentino. Es legal y los dólares quedan en una cuenta local.' },
      { heading: 'Dólar solidario / turista', body: 'Dólar oficial más impuestos adicionales (impuesto PAIS + retención de ganancias). Es el precio que pagan los argentinos al comprar dólares en bancos o al realizar consumos con tarjeta en el exterior.' },
      { heading: 'Dólar tarjeta', body: 'Tipo de cambio aplicado a consumos en moneda extranjera con tarjetas de crédito y débito argentinas. Incluye el tipo de cambio oficial más los recargos impositivos vigentes.' },
      { heading: 'Reservas internacionales', body: 'Activos en moneda extranjera que posee el banco central de un país. En Argentina, el nivel de reservas es un indicador clave de la capacidad del BCRA para sostener el tipo de cambio oficial.' },
      { heading: 'Spread', body: 'Diferencia entre el precio de compra y el precio de venta de una divisa. Un spread mayor indica menor liquidez o mayor riesgo en el mercado. En el contexto del dólar, se refiere a la diferencia entre compra y venta de cada tipo de cambio.' },
      { heading: 'TRM', body: 'Tasa Representativa del Mercado. En Colombia, es el tipo de cambio oficial calculado diariamente como el promedio ponderado de las operaciones de compra y venta de dólares entre intermediarios financieros.' },
      { heading: 'Tipo de cambio', body: 'Precio de una moneda expresado en términos de otra. Por ejemplo, el tipo de cambio USD/ARS indica cuántos pesos argentinos se necesitan para comprar un dólar estadounidense.' },
    ],
    relatedLinks: [
      { label: 'Cotización del dólar blue hoy', to: '/ar' },
      { label: '¿Qué es el dólar blue?', to: '/ar/que-es-el-dolar-blue' },
      { label: 'Dólar MEP vs CCL', to: '/ar/dolar-mep-vs-ccl' },
    ],
  },
];

const StaticContentPage = ({ config, theme, toggleTheme, t }: {
  config: ContentPageConfig; theme: Theme; toggleTheme: () => void; t: ThemeColors;
}) => {
  React.useEffect(() => {
    document.title = config.title;
    const setMeta = (sel: string, val: string) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute('content', val);
    };
    setMeta('meta[name="description"]', config.metaDescription);
    setMeta('meta[property="og:title"]', config.title);
    setMeta('meta[property="og:description"]', config.metaDescription);
    setMeta('meta[property="og:url"]', config.canonical);
    const canon = document.querySelector('link[rel="canonical"]');
    if (canon) canon.setAttribute('href', config.canonical);
  }, [config]);

  return (
    <div style={{ minHeight: '100vh', background: t.bg, fontFamily: FONT, color: t.text }}>
      <Header theme={theme} toggleTheme={toggleTheme} onReload={() => {}} activeCountry="ar" t={t} />
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 64px' }}>
        <Link to="/ar" style={{ color: t.textSecondary, textDecoration: 'none', fontSize: 13, display: 'inline-block', marginBottom: 24 }}>
          &larr; Volver a cotizaciones
        </Link>

        <article>
          <h1 style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.3, margin: '0 0 16px', color: t.text }}>
            {config.h1}
          </h1>
          <p style={{ fontSize: 16, color: t.textSecondary, marginBottom: 32, lineHeight: 1.6 }}>
            {config.intro}
          </p>

          {config.sections.map((sec, i) => (
            <section key={i} style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 12px', color: t.text }}>{sec.heading}</h2>
              {sec.body.split('\n\n').map((p, j) => (
                <p key={j} style={{ fontSize: 15, lineHeight: 1.75, color: t.text, marginBottom: 12 }}>{p}</p>
              ))}
            </section>
          ))}

          {config.relatedLinks.length > 0 && (
            <nav style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${t.cardBorder}` }} aria-label="Artículos relacionados">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: t.textSecondary, margin: '0 0 12px' }}>Artículos relacionados</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {config.relatedLinks.map((link, i) => (
                  <li key={i}>
                    <Link to={link.to} style={{ color: t.green, textDecoration: 'none', fontSize: 14 }}>
                      {link.label} &rarr;
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </article>

        <aside style={{ marginTop: 32 }} aria-label="Publicidad">
          <AdsComponent dataAdSlot='4458659545' />
        </aside>
      </main>
    </div>
  );
};

// --- Dashboard (per-country) ---

const Dashboard = ({ theme, toggleTheme, t }: {
  theme: Theme; toggleTheme: () => void; t: ThemeColors;
}) => {
  const { country: countryParam } = useParams<{ country: string }>();
  const navigate = useNavigate();
  const countryCode = countryParam || 'ar';
  const config = getCountryConfig(countryCode);

  // Redirect invalid country codes
  React.useEffect(() => {
    if (!VALID_CODES.has(countryCode)) {
      navigate('/ar', { replace: true });
    }
  }, [countryCode, navigate]);

  // SEO: update document head per country route
  React.useEffect(() => {
    if (!config) return;

    const titleByCountry: Record<string, string> = {
      ar: `Dólar Blue Hoy - Cotización del Dólar en Argentina`,
      br: `Dólar Hoje - Cotação do Dólar no Brasil`,
      mx: `Dólar Hoy México - Tipo de Cambio Actualizado`,
      cl: `Dólar Hoy Chile - Cotización del Dólar Observado`,
      co: `Dólar Hoy Colombia - TRM y Cotización Actualizada`,
      ve: `Dólar Hoy Venezuela - Oficial y Paralelo`,
      uy: `Dólar Hoy Uruguay - Cotización Actualizada`,
      bo: `Dólar Hoy Bolivia - Tipo de Cambio Oficial`,
    };

    const descByCountry: Record<string, string> = {
      ar: `Cotización del dólar blue, oficial, MEP, CCL, cripto, tarjeta y mayorista en Argentina. Compra, venta, spread y gráficos actualizados cada 2 minutos.`,
      br: `Cotação do dólar comercial no Brasil hoje. Compra, venda, spread e gráficos históricos atualizados a cada 2 minutos.`,
      mx: `Tipo de cambio del dólar en México hoy. Compra, venta, spread y gráficos históricos actualizados cada 2 minutos.`,
      cl: `Cotización del dólar observado en Chile hoy. Compra, venta, spread y gráficos históricos actualizados cada 2 minutos.`,
      co: `Precio del dólar TRM en Colombia hoy. Compra, venta, spread y gráficos históricos actualizados cada 2 minutos.`,
      ve: `Cotización del dólar oficial BCV y paralelo en Venezuela hoy. Precio actualizado cada 2 minutos con gráficos históricos.`,
      uy: `Cotización del dólar interbancario en Uruguay hoy. Compra, venta, spread y gráficos históricos actualizados cada 2 minutos.`,
      bo: `Tipo de cambio del dólar oficial y Binance en Bolivia hoy. Compra, venta y gráficos actualizados cada 2 minutos.`,
    };

    document.title = titleByCountry[config.code] || `Dólar Hoy en ${config.name} - Cotización Actualizada`;

    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };

    const desc = descByCountry[config.code] || `${config.subtitle}. Compra, venta, spread y gráficos actualizados cada 2 minutos.`;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', document.title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:url"]', `https://dolar.blue/${config.code}`);
    setMeta('meta[property="og:locale"]', config.locale.replace('-', '_'));
    setMeta('meta[name="geo.region"]', config.code.toUpperCase());

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://dolar.blue/${config.code}`);

    // Update structured data
    const ldScript = document.querySelector('script[type="application/ld+json"]');
    if (ldScript) {
      try {
        const schema = JSON.parse(ldScript.textContent || '{}');
        if (schema['@graph']) {
          for (const node of schema['@graph']) {
            if (node['@type'] === 'FinancialService') {
              node.areaServed = { '@type': 'Country', name: config.name };
            }
            if (node['@type'] === 'WebPage') {
              node.url = `https://dolar.blue/${config.code}`;
              node.name = document.title;
              node.description = desc;
              node.dateModified = new Date().toISOString().slice(0, 10);
              if (node.about?.['@type'] === 'ExchangeRateSpecification') {
                node.about.currency = config.currency;
              }
            }
          }
          ldScript.textContent = JSON.stringify(schema);
        }
      } catch {}
    }
  }, [config]);

  const [data, setData] = React.useState<DashboardData | null>(null);
  const [history, setHistory] = React.useState<HistoryData | null>(null);
  const [bluelyticsData, setBluelyticsData] = React.useState<BluelyticsEntry[] | null>(null);
  const [awesomeApiHist, setAwesomeApiHist] = React.useState<AwesomeApiHistEntry[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedRate, setSelectedRate] = React.useState<string>(config?.defaultRate || 'oficial');
  const [chartPeriod, setChartPeriod] = React.useState<ChartPeriod>('6M');

  const locale = config?.locale || 'es-AR';
  const currencySymbol = config?.currencySymbol || '$';

  const fetchData = React.useCallback(() => {
    setLoading(true);
    setError(null);

    const dataUrl = `/${countryCode}/data.json`;
    const historyUrl = `/${countryCode}/history.json`;

    const fetches: Promise<any>[] = [
      fetch(dataUrl).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
      fetch(historyUrl).then(r => r.ok ? r.json() : null).catch(() => null),
    ];

    // Bluelytics for Argentina
    if (countryCode === 'ar') {
      fetches.push(
        fetch('https://api.bluelytics.com.ar/v2/evolution.json?days=180')
          .then(r => r.ok ? r.json() : null).catch(() => null)
      );
      fetches.push(Promise.resolve(null)); // placeholder for awesomeApi slot
    } else {
      fetches.push(Promise.resolve(null)); // placeholder for bluelytics slot
      // AwesomeAPI historical for non-AR countries
      const pair = AWESOME_API_PAIRS[countryCode];
      if (pair) {
        fetches.push(
          fetch(`https://economia.awesomeapi.com.br/json/daily/${pair}/180`)
            .then(r => r.ok ? r.json() : null).catch(() => null)
        );
      } else {
        fetches.push(Promise.resolve(null));
      }
    }

    Promise.all(fetches)
      .then(([dashData, histData, blueData, awesomeData]) => {
        setData(dashData);
        setHistory(histData);
        setBluelyticsData(blueData || null);
        setAwesomeApiHist(awesomeData || null);
      })
      .catch(err => setError(String(err)))
      .finally(() => setLoading(false));
  }, [countryCode]);

  // Re-fetch when country changes
  React.useEffect(() => {
    fetchData();
    setSelectedRate(config?.defaultRate || 'oficial');
    setChartPeriod('6M');
  }, [countryCode, fetchData, config?.defaultRate]);

  // Build chart data for selected rate + period
  const chartData = React.useMemo(() => {
    const cutoff = getCutoff(chartPeriod);
    const points: { timestamp: number; sell: number }[] = [];

    // Bluelytics backfill for AR oficial/blue
    if (countryCode === 'ar' && (selectedRate === 'oficial' || selectedRate === 'blue') && bluelyticsData) {
      const sourceLabel = selectedRate === 'oficial' ? 'Oficial' : 'Blue';
      for (const entry of bluelyticsData) {
        const ts = new Date(entry.date).getTime();
        if (ts >= cutoff && entry.source === sourceLabel && entry.value_sell) {
          points.push({ timestamp: ts, sell: entry.value_sell });
        }
      }
    }

    // AwesomeAPI historical backfill for non-AR countries (covers the default rate)
    if (countryCode !== 'ar' && awesomeApiHist && selectedRate === (config?.defaultRate || 'oficial')) {
      for (const entry of awesomeApiHist) {
        const ts = Number(entry.timestamp) * 1000;
        const sell = parseFloat(entry.ask);
        if (!isNaN(ts) && ts >= cutoff && !isNaN(sell)) {
          points.push({ timestamp: ts, sell });
        }
      }
    }

    // Self-stored history
    if (history?.snapshots) {
      for (const snap of history.snapshots) {
        const ts = new Date(snap.timestamp).getTime();
        if (ts >= cutoff && snap.rates[selectedRate]) {
          const sell = snap.rates[selectedRate].sell;
          if (sell !== null) {
            points.push({ timestamp: ts, sell });
          }
        }
      }
    }

    points.sort((a, b) => a.timestamp - b.timestamp);
    const seen = new Set<number>();
    return points.filter(p => {
      const key = Math.round(p.timestamp / 60000);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [selectedRate, chartPeriod, history, bluelyticsData, awesomeApiHist, countryCode, config?.defaultRate]);

  if (!config) return null;

  const rates = data?.rates ? Object.entries(data.rates) : [];
  const currentRate = data?.rates?.[selectedRate];

  return (
    <>
      <Header theme={theme} toggleTheme={toggleTheme} onReload={fetchData} activeCountry={countryCode} t={t} />

      <main style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px 48px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: t.textSecondary, fontSize: 14 }}>
            Cargando cotizaciones...
          </div>
        )}

        {error && (
          <div style={{
            background: theme === 'dark' ? '#2d1215' : '#ffebe9',
            border: `1px solid ${theme === 'dark' ? '#f8514926' : '#f851491a'}`,
            borderRadius: 6, padding: '12px 16px', color: t.red, fontSize: 14, marginBottom: 24,
          }}>
            Error cargando datos: {error}
          </div>
        )}

        {!loading && !error && currentRate && (
          <>
            <div style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
              <HeroSection rate={currentRate} currencySymbol={currencySymbol} locale={locale} t={t} />
              <ChartSection
                chartData={chartData}
                period={chartPeriod}
                setPeriod={setChartPeriod}
                rateName={currentRate.name}
                locale={locale}
                currencySymbol={currencySymbol}
                t={t}
              />
            </div>

            <RateTable
              rates={rates}
              selectedKey={selectedRate}
              onSelect={setSelectedRate}
              currencySymbol={currencySymbol}
              locale={locale}
              t={t}
            />

            <NewsSection countryCode={countryCode} t={t} />

            {countryCode === 'ar' && <FaqSection t={t} />}
          </>
        )}

        {data?.fetchedAt && (
          <footer style={{
            textAlign: 'center', marginTop: 32, paddingTop: 20,
            borderTop: `1px solid ${t.cardBorder}`, fontSize: 11, color: t.textMuted,
          }}>
            <p>Actualizado: {formatTime(data.fetchedAt, locale)}</p>
            <p style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              {COUNTRY_LIST.map(c => (
                <Link key={c.code} to={`/${c.code}`} style={{ color: t.textMuted, textDecoration: 'none', fontSize: 11 }}>
                  {c.flag} {c.name}
                </Link>
              ))}
            </p>
            <p style={{ marginTop: 8 }}>{config.subtitle}</p>
          </footer>
        )}

        <aside style={{ marginTop: 24 }} aria-label="Publicidad">
          <AdsComponent dataAdSlot='4458659545' />
        </aside>
      </main>
    </>
  );
};

// --- App (Router) ---

export const App = () => {
  const [theme, setTheme] = React.useState<Theme>(getStoredTheme);
  const t = themes[theme];

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('dblue-theme', next);
  };

  return (
    <div style={{ minHeight: '100vh', background: t.bg, fontFamily: FONT, color: t.text }}>
      <Routes>
        {CONTENT_PAGES.map(cfg => (
          <Route key={cfg.path} path={cfg.path} element={<StaticContentPage config={cfg} theme={theme} toggleTheme={toggleTheme} t={t} />} />
        ))}
        <Route path="/:country/:date/:slug" element={<ArticlePage theme={theme} toggleTheme={toggleTheme} t={t} />} />
        <Route path="/:country" element={<Dashboard theme={theme} toggleTheme={toggleTheme} t={t} />} />
        <Route path="/" element={<Navigate to="/ar" replace />} />
      </Routes>
    </div>
  );
};
